import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import { DevicesOther as DeviceIcon, UsbOff as DeviceOffIcon, Share as ShareIcon, Print as PrintIcon } from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { useDeviceStore } from '@/store/useDeviceStore';
import { useThemeMode } from '@/lib/themes/ThemeProvider';
import { useTheme } from '@mui/material/styles';

interface WizardHeaderProps {
  showCancelButton?: boolean;
  showCloseButton?: boolean;
  showSharePrint?: boolean;
  showDeviceStatus?: boolean;
  onClose?: () => void;
  onCancel?: () => void;
}

export function MeasurementWizardHeader({
  showCancelButton = false,
  showCloseButton = false,
  showSharePrint = false,
  showDeviceStatus = true,
  onClose,
  onCancel
}: WizardHeaderProps) {
  const navigate = useNavigate();
  const resetWizard = useMeasurementWizardStore((s) => s.resetWizard);
  const selectedDevice = useMeasurementWizardStore((s) => s.selectedDevice);
  const deviceInfo = useDeviceStore((s) => s.deviceInfo);
  const [shareMenuAnchor, setShareMenuAnchor] = useState<null | HTMLElement>(null);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      // Fallback to old behavior
      if (confirm('Are you sure you want to cancel this test? All progress will be lost.')) {
        resetWizard();
        navigate('/');
      }
    }
  };

  const handleClose = () => {
    resetWizard();
    if (onClose) {
      onClose();
    } else {
      navigate('/');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopyLink = () => {
    const testId = 'TEST-' + Date.now(); // Mock test ID
    const link = `${window.location.origin}/tests/${testId}`;
    navigator.clipboard.writeText(link);
    setShareMenuAnchor(null);
    // TODO: Show toast notification
  };

  const { mode } = useThemeMode();
  const theme = useTheme();
  const logoSrc = mode === 'dark' ? '/ediphor-logo-light.svg' : '/ediphor-logo.svg';
  const connectionState = useDeviceStore((s) => s.connectionState);
  const isConnected = connectionState === 'connected' || connectionState === 'busy';
  const storeLabel = useDeviceStore((s) => s.getDeviceLabel());
  const deviceLabel = (() => {
    // prefer the live store label when a device is connected
    if (deviceInfo && storeLabel && storeLabel !== 'Connected') {
      return storeLabel;
    }

    // fall back to the device captured in wizard state
    if (selectedDevice) {
      if (selectedDevice.nickname && selectedDevice.nickname.trim()) {
        return selectedDevice.nickname;
      }
      if (selectedDevice.serialNumber && selectedDevice.serialNumber !== '0') {
        return `Reader ${selectedDevice.serialNumber}`;
      }
      if (selectedDevice.uid) {
        return selectedDevice.uid;
      }
    }

    return 'Unknown';
  })();

  return (
    <Box sx={{
      height: '64px',
      minHeight: '64px',
      px: { xs: 2, sm: 4 },
      bgcolor: 'background.paper',
      backgroundImage: theme.customHeader.backgroundImage,
      backgroundSize: theme.customHeader.backgroundSize,
      backgroundPosition: theme.customHeader.backgroundPosition,
      backgroundRepeat: theme.customHeader.backgroundRepeat,
      borderBottom: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      overflow: theme.customHeader.overflow,
    }}>
      {/* Logo and LabCloud Text */}
      <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 2, sm: 4 } }}>
        <Box
          component="img"
          src={logoSrc}
          alt="Ediphor"
          sx={{ height: 32, marginRight: 1 }}
          onError={(e) => {
            // Fallback if logo not available
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          LabCloud
        </Typography>
      </Box>

      {/* Device Status */}
      {showDeviceStatus && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexGrow: 1,
        }}>
          {isConnected ? (
            <DeviceIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          ) : (
            <DeviceOffIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
          )}
          <Box sx={{ fontSize: '0.875rem', fontWeight: 500, color: isConnected ? 'text.primary' : 'text.disabled' }}>
            {isConnected ? deviceLabel : 'No device connected'}
          </Box>
        </Box>
      )}
      {!showDeviceStatus && <Box sx={{ flexGrow: 1 }} />}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {showSharePrint && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ShareIcon />}
              onClick={(e) => setShareMenuAnchor(e.currentTarget)}
              sx={{
                textTransform: 'none',
                bgcolor: theme.customHeader.buttonBgColor,
                '&:hover': { bgcolor: theme.customHeader.buttonHoverBgColor }
              }}
            >
              Share
            </Button>
            <Menu
              anchorEl={shareMenuAnchor}
              open={Boolean(shareMenuAnchor)}
              onClose={() => setShareMenuAnchor(null)}
            >
              <MenuItem onClick={handleCopyLink}>Copy link</MenuItem>
              <MenuItem disabled>Email</MenuItem>
              <MenuItem disabled>WhatsApp</MenuItem>
            </Menu>

            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                textTransform: 'none',
                bgcolor: theme.customHeader.buttonBgColor,
                '&:hover': { bgcolor: theme.customHeader.buttonHoverBgColor }
              }}
            >
              Print
            </Button>
          </>
        )}

        {showCancelButton && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancel}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              bgcolor: theme.customHeader.buttonBgColor,
              '&:hover': { bgcolor: 'error.main', color: 'white' }
            }}
          >
            Cancel test
          </Button>
        )}

        {showCloseButton && (
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.primary',
              borderColor: 'divider',
              bgcolor: theme.customHeader.buttonBgColor,
              '&:hover': { bgcolor: theme.customHeader.buttonHoverBgColor }
            }}
          >
            Close test
          </Button>
        )}
      </Box>
    </Box>
  );
}
